import { useEffect, useMemo, useState } from 'react';

interface BloqueHorario {
  id: number;
  hora: string;
  lunes: number;
  martes: number;
  miercoles: number;
  jueves: number;
  viernes: number;
  sabado: number;
  domingo: number;
}

interface Props {
  bloquesIniciales: BloqueHorario[];
  diasActivos: string[];
  onSave: (cupos: Record<string, Record<string, number>>) => void;
  onClose: () => void;
}

const diasColumnas = [
  { key: 'lunes', label: 'LUN', labelFull: 'Lunes' },
  { key: 'martes', label: 'MAR', labelFull: 'Martes' },
  { key: 'miercoles', label: 'MIE', labelFull: 'Miércoles' },
  { key: 'jueves', label: 'JUE', labelFull: 'Jueves' },
  { key: 'viernes', label: 'VIE', labelFull: 'Viernes' },
  { key: 'sabado', label: 'SAB', labelFull: 'Sábado' },
  { key: 'domingo', label: 'DOM', labelFull: 'Domingo' },
] as const;

const dayLabelsDb = {
  lunes: 'Lunes',
  martes: 'Martes',
  miercoles: 'Miércoles',
  jueves: 'Jueves',
  viernes: 'Viernes',
  sabado: 'Sábado',
  domingo: 'Domingo',
} as const;

const allDayKeys = diasColumnas.map((d) => d.key);

const obtenerLunesDeSemana = (fecha: Date): Date => {
  const dia = fecha.getDay();
  const diff = dia === 0 ? -6 : 1 - dia;
  const lunes = new Date(fecha);
  lunes.setDate(fecha.getDate() + diff);
  lunes.setHours(0, 0, 0, 0);
  return lunes;
};

const formatearFecha = (fecha: Date): string => {
  const dia = fecha.getDate();
  const mes = fecha.getMonth() + 1;
  return `${dia}/${mes.toString().padStart(2, '0')}`;
};

const obtenerNombreMes = (fecha: Date): string => {
  const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  return meses[fecha.getMonth()];
};

export default function EditarCuposModal({ bloquesIniciales, diasActivos, onSave, onClose }: Props) {
  const [bloques, setBloques] = useState<BloqueHorario[]>(() => bloquesIniciales.map((b) => ({ ...b })));
  const [saved, setSaved] = useState(false);
  const [cupoMasivo, setCupoMasivo] = useState('');
  const [diasMasivo, setDiasMasivo] = useState<string[]>([]);
  const [semanaActual, setSemanaActual] = useState<Date>(() => obtenerLunesDeSemana(new Date()));

  const diasActivosSet = useMemo(() => new Set(diasActivos.filter((d) => allDayKeys.includes(d as any))), [diasActivos]);

  useEffect(() => {
    setDiasMasivo(diasActivos.filter((d) => allDayKeys.includes(d as any)));
  }, [diasActivos]);

  useEffect(() => {
    if (diasActivosSet.size === 0) return;
    setBloques((prev) =>
      prev.map((bloque) => {
        const updated = { ...bloque } as any;
        for (const dayKey of allDayKeys) {
          if (!diasActivosSet.has(dayKey)) {
            updated[dayKey] = 0;
          }
        }
        return updated as BloqueHorario;
      })
    );
  }, [diasActivosSet]);

  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);

  const fechasSemana = diasColumnas.map((_dia, index) => {
    const fecha = new Date(semanaActual);
    fecha.setDate(semanaActual.getDate() + index);
    return fecha;
  });

  const esHoy = (fecha: Date): boolean => fecha.getTime() === hoy.getTime();

  const cambiarSemana = (direccion: number) => {
    const nuevaSemana = new Date(semanaActual);
    nuevaSemana.setDate(semanaActual.getDate() + direccion * 7);
    setSemanaActual(nuevaSemana);
  };

  const irASemanaActual = () => {
    setSemanaActual(obtenerLunesDeSemana(new Date()));
  };

  const handleCupoChange = (bloqueId: number, dia: string, value: string) => {
    if (!diasActivosSet.has(dia)) return;
    const num = value === '' ? 0 : Math.max(0, Math.min(99, parseInt(value, 10) || 0));
    setBloques((prev) => prev.map((b) => (b.id === bloqueId ? { ...b, [dia]: num } : b)));
  };

  const getDayValue = (bloque: BloqueHorario, dayKey: keyof Pick<BloqueHorario, 'lunes' | 'martes' | 'miercoles' | 'jueves' | 'viernes' | 'sabado' | 'domingo'>) => {
    if (!diasActivosSet.has(dayKey)) return 0;
    return bloque[dayKey] || 0;
  };

  const handleSave = () => {
    const cuposObj: Record<string, Record<string, number>> = {};

    bloques.forEach((bloque) => {
      cuposObj[bloque.hora] = {
        [dayLabelsDb.lunes]: getDayValue(bloque, 'lunes'),
        [dayLabelsDb.martes]: getDayValue(bloque, 'martes'),
        [dayLabelsDb.miercoles]: getDayValue(bloque, 'miercoles'),
        [dayLabelsDb.jueves]: getDayValue(bloque, 'jueves'),
        [dayLabelsDb.viernes]: getDayValue(bloque, 'viernes'),
        [dayLabelsDb.sabado]: getDayValue(bloque, 'sabado'),
        [dayLabelsDb.domingo]: getDayValue(bloque, 'domingo'),
      };
    });

    onSave(cuposObj);
    setSaved(true);
    setTimeout(() => onClose(), 800);
  };

  const toggleDiaMasivo = (dia: string) => {
    if (!diasActivosSet.has(dia)) return;
    setDiasMasivo((prev) => (prev.includes(dia) ? prev.filter((d) => d !== dia) : [...prev, dia]));
  };

  const aplicarCupoMasivo = () => {
    const valor = parseInt(cupoMasivo, 10);
    if (isNaN(valor) || valor < 0 || valor > 99) return;

    setBloques((prev) =>
      prev.map((b) => {
        const updated = { ...b } as any;
        for (const dia of diasMasivo) {
          if (diasActivosSet.has(dia)) {
            updated[dia] = valor;
          }
        }
        return updated as BloqueHorario;
      })
    );

    setCupoMasivo('');
  };

  const limpiarTodo = () => {
    setBloques((prev) =>
      prev.map((b) => ({
        ...b,
        lunes: 0,
        martes: 0,
        miercoles: 0,
        jueves: 0,
        viernes: 0,
        sabado: 0,
        domingo: 0,
      }))
    );
  };

  const esSemanaActual = obtenerLunesDeSemana(new Date()).getTime() === semanaActual.getTime();

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose}></div>

        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-6xl mx-auto">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Editar Cupos</h3>
                <p className="text-sm text-gray-500 mt-1">Modifica la capacidad máxima por franja horaria y día</p>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                <i className="ri-close-line text-xl text-gray-500"></i>
              </button>
            </div>
          </div>

          <div className="px-6 py-4 bg-gradient-to-r from-teal-50 to-cyan-50 border-b border-teal-100">
            <div className="flex items-center justify-between">
              <button
                onClick={() => cambiarSemana(-1)}
                className="p-2 hover:bg-white/60 rounded-lg transition-colors cursor-pointer"
                title="Semana anterior"
              >
                <i className="ri-arrow-left-s-line text-xl text-teal-700"></i>
              </button>

              <div className="flex items-center gap-3">
                <div className="text-center">
                  <div className="text-sm font-semibold text-teal-900">
                    {formatearFecha(fechasSemana[0])} - {formatearFecha(fechasSemana[6])} {obtenerNombreMes(fechasSemana[0])}{' '}
                    {fechasSemana[0].getFullYear()}
                  </div>
                  <div className="text-xs text-teal-600 mt-0.5">Semana del {fechasSemana[0].getDate()} al {fechasSemana[6].getDate()}</div>
                </div>

                {!esSemanaActual && (
                  <button
                    onClick={irASemanaActual}
                    className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-xs font-medium hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
                  >
                    <i className="ri-calendar-check-line"></i>
                    Hoy
                  </button>
                )}
              </div>

              <button
                onClick={() => cambiarSemana(1)}
                className="p-2 hover:bg-white/60 rounded-lg transition-colors cursor-pointer"
                title="Semana siguiente"
              >
                <i className="ri-arrow-right-s-line text-xl text-teal-700"></i>
              </button>
            </div>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">Asignar cupo masivo:</span>
                <input
                  type="number"
                  min="0"
                  max="99"
                  value={cupoMasivo}
                  onChange={(e) => setCupoMasivo(e.target.value)}
                  placeholder="Ej: 25"
                  className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm text-center"
                />
              </div>

              <div className="flex items-center gap-1">
                {diasColumnas.map((dia) => {
                  const isActive = diasActivosSet.has(dia.key);
                  return (
                    <button
                      key={dia.key}
                      onClick={() => toggleDiaMasivo(dia.key)}
                      disabled={!isActive}
                      className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
                        !isActive
                          ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'
                          : diasMasivo.includes(dia.key)
                          ? 'bg-teal-500 text-white cursor-pointer'
                          : 'bg-white text-gray-500 border border-gray-300 cursor-pointer'
                      }`}
                    >
                      {dia.label}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={aplicarCupoMasivo}
                disabled={!cupoMasivo || diasMasivo.length === 0}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Aplicar
              </button>

              <div className="ml-auto">
                <button
                  onClick={limpiarTodo}
                  className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-delete-bin-line mr-1"></i>
                  Limpiar todo
                </button>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 max-h-[50vh] overflow-y-auto">
            <table className="w-full">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-3 text-sm font-semibold text-gray-600 w-40">HORARIO</th>
                  {diasColumnas.map((dia, index) => {
                    const fecha = fechasSemana[index];
                    const hoyMarcado = esHoy(fecha);
                    return (
                      <th key={dia.key} className="text-center py-3 px-2">
                        <div className={`flex flex-col items-center gap-1 ${hoyMarcado ? 'relative' : ''}`}>
                          {hoyMarcado && (
                            <span className="absolute -top-1 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-teal-500 text-white text-[10px] font-bold rounded-full uppercase tracking-wide">
                              Hoy
                            </span>
                          )}
                          <span className={`text-sm font-semibold ${hoyMarcado ? 'text-teal-600 mt-4' : 'text-gray-600'}`}>{dia.label}</span>
                          <span className={`text-xs font-medium ${hoyMarcado ? 'text-teal-500' : 'text-gray-500'}`}>{formatearFecha(fecha)}</span>
                          <span className={`text-[10px] ${hoyMarcado ? 'text-teal-400' : 'text-gray-400'}`}>{dia.labelFull}</span>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {bloques.map((bloque) => (
                  <tr key={bloque.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                    <td className="py-2.5 px-3 text-sm font-medium text-gray-700">{bloque.hora}</td>
                    {diasColumnas.map((dia, index) => {
                      const isDayActive = diasActivosSet.has(dia.key);
                      const valor = isDayActive ? ((bloque as any)[dia.key] as number) || 0 : 0;
                      const fecha = fechasSemana[index];
                      const hoyMarcado = esHoy(fecha);
                      return (
                        <td key={dia.key} className={`py-2.5 px-2 text-center ${hoyMarcado ? 'bg-teal-50/50' : ''}`}>
                          <input
                            type="number"
                            min="0"
                            max="99"
                            disabled={!isDayActive}
                            value={valor === 0 ? '' : valor}
                            onChange={(e) => handleCupoChange(bloque.id, dia.key, e.target.value)}
                            placeholder={isDayActive ? '0' : '-'}
                            className={`w-16 h-10 text-center border rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-teal-500 transition-colors mx-auto ${
                              !isDayActive
                                ? 'border-gray-200 bg-gray-50 text-gray-300 cursor-not-allowed'
                                : valor > 0
                                ? hoyMarcado
                                  ? 'border-teal-400 bg-teal-100 text-teal-800'
                                  : 'border-teal-300 bg-teal-50 text-teal-700'
                                : hoyMarcado
                                ? 'border-teal-200 bg-teal-50/50 text-gray-400'
                                : 'border-gray-200 bg-gray-50 text-gray-400'
                            }`}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="px-6 py-3 border-t border-gray-100">
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <div className="flex items-center gap-1.5">
                <span className="w-4 h-4 rounded bg-teal-50 border border-teal-300"></span>
                <span>Con cupos asignados</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="w-4 h-4 rounded bg-gray-50 border border-gray-200"></span>
                <span>Sin cupos (cerrado)</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="px-1.5 py-0.5 bg-teal-500 text-white text-[9px] font-bold rounded">HOY</span>
                <span>Día actual</span>
              </div>
              <span className="ml-auto">Valores entre 0 y 99</span>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
            <button
              onClick={onClose}
              className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saved}
              className={`px-5 py-2 text-sm font-medium text-white rounded-lg transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                saved ? 'bg-green-500' : 'bg-teal-600 hover:bg-teal-700'
              }`}
            >
              {saved ? (
                <>
                  <i className="ri-check-line"></i>
                  Guardado
                </>
              ) : (
                <>
                  <i className="ri-save-line"></i>
                  Guardar Cupos
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
